import { CONST } from "../CONST";

export interface TutorialHandConfig {
    spawnPos: {x: number, y: number},

    moveAnim?: {
        movePos: {x: number, y: number},
        time: number, //in ms
    },
    depth: number,
}

const handConf = {
    handTexture: CONST.GENERIC.TUTORIAL_HAND,
    origin: {x: 0.7, y: 0.26},
    pressedPos: {x: 32, y: -35, angle: 0},
    hoverPos: {x: 40, y: -40, angle: 30},
    
    startAnim: {
        ease: "Quad.easeInOut",
        duration: 300,
        delay: 100,
        hold: 150,
    },
    moveAnim: {
        ease: "Quad.easeInOut",
        delay: 150,
        hold: 150,
    },
    endAnim: {
        ease: "Quad.easeInOut",
        duration: 300,
        delay: 150,
        hold: 300,
    },
}

enum HandState {
    Deactivated,
    Starting,
    Moving,
    Ending,
}

export class TutorialHand extends Phaser.GameObjects.Container {
    //TODO: functions onShow onHide -> so we can add custom functionality (e.g. override trash/serve highlighting)
    private config : TutorialHandConfig;

    private currentState: HandState;
    private hasMoveAnim: boolean;
    private handImage: Phaser.GameObjects.Image;

    private handTween !: Phaser.Tweens.Tween;
    private moveTween !: Phaser.Tweens.Tween;
    private fadeTween !: Phaser.Tweens.Tween;

    constructor(config: TutorialHandConfig, scene: Phaser.Scene){
        super(scene, config.spawnPos.x, config.spawnPos.y);
        this.setDepth(config.depth);

        this.config = config;
        this.currentState = HandState.Deactivated;
        this.setHandState(HandState.Deactivated);
        
        this.hasMoveAnim = typeof config.moveAnim !== 'undefined';

        this.handImage = scene.add.image(handConf.pressedPos.x, handConf.pressedPos.y, handConf.handTexture)
                            .setOrigin(handConf.origin.x, handConf.origin.y)
                            .setAngle(handConf.pressedPos.angle);

        // this.displayDebugGizmos(scene, config)

        this.add([this.handImage]);
        scene.add.existing(this);
    }

    private setHandState(newState: HandState){
        this.currentState = newState;
        switch(newState){
            case HandState.Deactivated:
                this.onDeactivated();
                break;           
            case HandState.Starting:
                this.onStarting();
                break;            
            case HandState.Moving:
                this.onMoving();
                break;            
            case HandState.Ending:
                this.onEnding();
                break;
        }
    }

    private fade(fadeIn: boolean){
        if (this.fadeTween){
            this.fadeTween.stop();
        }

        this.fadeTween = this.scene.tweens.add({
            targets: this,
            alpha: fadeIn ? 1 : 0,
            duration: 200,
            ease: "Quad.easeInOut",
        });
    }

    private onDeactivated(){
        //Hide hand and deactivate all tweens

        if (this.handTween){
            this.handTween.stop();
        }
        if (this.moveTween){
            this.moveTween.stop();
        }

        this.fade(false);       
    }

    private onStarting(){
        //Set to start position + animate down - press

        this.fade(true);

        this.setPosition(this.config.spawnPos.x, this.config.spawnPos.y);
        this.handImage.setPosition(handConf.hoverPos.x, handConf.hoverPos.y).setAngle(handConf.hoverPos.angle);

        if (this.handTween){
            this.handTween.stop();
        }

        this.handTween = this.scene.tweens.add({
            targets: this.handImage,
            x: handConf.pressedPos.x,
            y: handConf.pressedPos.y,
            angle: handConf.pressedPos.angle,
            ease: handConf.startAnim.ease,
            duration: handConf.startAnim.duration,
            repeat: 0,
            yoyo: false,
            delay: handConf.startAnim.delay,
            hold: handConf.startAnim.hold,
            onComplete: () => {
                this.setHandState(HandState.Moving);
            }
        });
    }

    private onMoving(){
        //Move container from start to end

        if (!this.hasMoveAnim || !this.config.moveAnim){
            this.setHandState(HandState.Ending);
            return;
        }

        if (this.moveTween){
            this.moveTween.stop();
        }

        this.moveTween = this.scene.tweens.add({
            targets: this,
            x: this.config.moveAnim.movePos.x,
            y: this.config.moveAnim.movePos.y,
            ease: handConf.moveAnim.ease,
            duration: this.config.moveAnim.time,
            repeat: 0,
            yoyo: false,
            delay: handConf.moveAnim.delay,
            hold: handConf.moveAnim.hold,
            onComplete: () => {
                this.setHandState(HandState.Ending);
            }
        });
    }

    private onEnding(){
        //animate hand up - release

        this.handImage.setPosition(handConf.pressedPos.x, handConf.pressedPos.y).setAngle(handConf.pressedPos.angle);

        if (this.handTween){
            this.handTween.stop();
        }

        this.handTween = this.scene.tweens.add({
            targets: this.handImage,
            x: handConf.hoverPos.x,
            y: handConf.hoverPos.y,
            angle: handConf.hoverPos.angle,
            ease: handConf.endAnim.ease,
            duration: handConf.endAnim.duration,
            repeat: 0,
            yoyo: false,
            delay: handConf.endAnim.delay,
            onComplete: () => {
                if (this.hasMoveAnim){
                    this.fade(false);
                }
                setTimeout(() => {
                    if (this.currentState != HandState.Ending) return; //Fix -> return before going to starting state if we deactivate hand while this timeout is running
                    this.setHandState(HandState.Starting);
                }, handConf.endAnim.hold);
            }
        });
    }

    public show(){
        if (this.currentState != HandState.Deactivated){
            return;
        }

        this.setHandState(HandState.Starting);
    }

    public hide(){
        if (this.currentState == HandState.Deactivated){
            return;
        }

        this.setHandState(HandState.Deactivated);
    }

    public isActivated(){
        return this.currentState != HandState.Deactivated;
    }

    displayDebugGizmos(scene: Phaser.Scene, config: TutorialHandConfig){
        let test = scene.add.image(config.spawnPos.x, config.spawnPos.y, CONST.BUTTONS.BACK).setTint(0x00ff00).setDepth(20).setScale(0.2);

        if (config.moveAnim){
            let test2 = scene.add.image(config.moveAnim.movePos.x, config.moveAnim.movePos.y, CONST.BUTTONS.BACK)
            .setTint(0xffff00).setDepth(20).setScale(0.2);
        }
    }
}